// Shared "all-in" (fee-inclusive) breakdown derivation for a SAVED
// booking snapshot (the booking-detail API `info` object).
//
// The booking row only stores RAW pro-rate line items plus the platform
// fee / VAT / charges as separate amounts (the historical "transparent"
// model). The customer, however, is quoted an all-in rate everywhere
// else (£18/h, not £15/h + fee), so the on-screen breakdown and the
// invoice present it the same way the basket now does:
//
//   • Item / Addon totals are shown at the all-in rate (pro rate ×
//     fee markup), so £18/h × 2 = £36 — matching the per-line rate.
//   • The platform fee and the VAT inside it are folded INTO the item
//     total and surfaced only as an informational "includes VAT" line,
//     never added again.
//   • Discounts are rescaled to the all-in base and taken as the
//     reconciling REMAINDER against the stored `total_amount`, so the
//     visible lines always sum exactly to the total — no 1p drift, and
//     legacy bookings (where no fee was ever added on top) degrade
//     cleanly to the raw numbers (fee markup collapses to 1).
//
// Used by both the booking-detail screen and the downloadable invoice
// so the two never disagree.

const round2 = (n: number) => Math.round(n * 100) / 100;

export type AllInItem = Record<string, any> & {
  customer_unit_price: number;
  customer_line_total: number;
};

export type AllInBreakdown = {
  feeMarkup: number;
  items: AllInItem[];
  itemsTotal: number;
  addonsTotal: number;
  membershipDiscount: number;
  voucherDiscount: number;
  legacyDiscount: number;
  isLegacyVoucher: boolean;
  includedTax: number;
  taxPercentage: number;
  taxName: string;
  charges: Array<Record<string, any>>;
  chargesTotal: number;
  walletAmount: number;
  cardAmount: number;
  totalAmount: number;
};

export function toAllInBreakdown(info: any): AllInBreakdown {
  const items = (info?.items ?? []) as any[];
  // Saved booking-detail items only carry the relation OBJECTS
  // (it.service / it.service_addon), not the scalar FK ids that the live
  // /quote items carry. Classify off either so the fee markup folds into
  // the item prices on the saved-booking screens too (otherwise the
  // platform fee never gets distributed and the breakdown doesn't sum to
  // the total).
  const isAddon = (it: any) => !!(it.service_addon_id ?? it.service_addon?.id);
  const isService = (it: any) =>
    !!(it.service_id ?? it.service?.id) && !isAddon(it);
  const rawItemsTotal = items
    .filter(isService)
    .reduce((s, it) => s + Number(it.line_total ?? 0), 0);
  const rawAddonsTotal = items
    .filter(isAddon)
    .reduce((s, it) => s + Number(it.line_total ?? 0), 0);

  const membershipAmt = Number(info?.membership_discount_amount ?? 0);
  const voucherAmt = Number(info?.voucher_discount_amount ?? 0);
  // Legacy bookings predate the split membership/voucher columns and only
  // carry a single combined `discount_amount`.
  const legacyAmt =
    membershipAmt === 0 && voucherAmt === 0
      ? Number(info?.discount_amount ?? 0)
      : 0;

  const taxAmt = Number(info?.tax_amount ?? 0);
  const charges = (info?.charges_breakdown ?? []) as any[];
  const chargesTotal = round2(
    charges.reduce((s, c) => s + Number(c.amount ?? 0), 0),
  );
  const totalAmount = Number(info?.total_amount ?? 0);

  // Residual platform fee (ex-VAT) that makes the transparent breakdown
  // reconcile to total_amount — the same self-reconciling residual the
  // old on-screen / invoice breakdowns used. For +20%-model bookings this
  // is the fee added on top; for legacy bookings it collapses to ~0.
  const platformFeeExVat = round2(
    totalAmount -
      (rawItemsTotal +
        rawAddonsTotal -
        membershipAmt -
        voucherAmt -
        legacyAmt +
        taxAmt +
        chargesTotal),
  );
  // Markup folded into the item total = the platform fee EX-VAT only. VAT is
  // now a SEPARATE line ADDED on top (June 2026), so it is no longer folded
  // into the items here.
  const grossFee = round2(Math.max(0, platformFeeExVat));
  const discountedBase =
    rawItemsTotal + rawAddonsTotal - membershipAmt - voucherAmt - legacyAmt;
  // (discountedBase + grossFee) / discountedBase — the markup that turns
  // the pro rate into the all-in rate. Collapses to 1 when no fee.
  const feeMarkup =
    discountedBase > 0 ? (discountedBase + grossFee) / discountedBase : 1;

  const itemsTotal = round2(rawItemsTotal * feeMarkup);
  const addonsTotal = round2(rawAddonsTotal * feeMarkup);

  // Reconciling remainder: itemsTotal + addonsTotal − discount + VAT + charges
  // === totalAmount. VAT is now ADDED on top (part of totalAmount), so add it
  // back here so the residual isolates the real discount (VAT + charges
  // cancel). Legacy VAT-inclusive bookings reconcile to the same total too —
  // just shown as items-ex-VAT plus a VAT line.
  const discountTotal = round2(
    Math.max(0, itemsTotal + addonsTotal + chargesTotal + taxAmt - totalAmount),
  );
  const rawDiscountSum = membershipAmt + voucherAmt + legacyAmt;
  const scale = rawDiscountSum > 0 ? discountTotal / rawDiscountSum : 0;
  const membershipDiscount = round2(membershipAmt * scale);
  const voucherDiscount = round2(voucherAmt * scale);
  // Legacy line takes the remainder so the parts sum exactly to the whole.
  const legacyDiscount = round2(
    discountTotal - membershipDiscount - voucherDiscount,
  );

  const allInItems: AllInItem[] = items.map((it) => ({
    ...it,
    customer_unit_price: round2(Number(it.unit_price ?? 0) * feeMarkup),
    customer_line_total: round2(Number(it.line_total ?? 0) * feeMarkup),
  }));

  return {
    feeMarkup,
    items: allInItems,
    itemsTotal,
    addonsTotal,
    membershipDiscount,
    voucherDiscount,
    legacyDiscount,
    isLegacyVoucher: !!info?.customer_voucher,
    includedTax: taxAmt,
    taxPercentage: Number(info?.tax_percentage ?? 0),
    taxName: info?.tax_name || 'VAT',
    charges,
    chargesTotal,
    walletAmount: Number(info?.wallet_amount ?? 0),
    cardAmount: Number(
      info?.card_amount ??
        Math.max(0, totalAmount - Number(info?.wallet_amount ?? 0)),
    ),
    totalAmount,
  };
}
